import React, { useState } from 'react';
import { authFetch } from './authFetch';

function Debug() {
  const [userData, setUserData] = useState(null);
  const [groupData, setGroupData] = useState(null);
  const [csrfToken, setCsrfToken] = useState('');
  const [cookies, setCookies] = useState('');
  const [loading, setLoading] = useState(false);

  // Check user info
  const checkUserInfo = async () => {
    setLoading(true);
    try {
      const BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const response = await authFetch(`${BASE_URL}/api/auth/debug/`);
      
      if (response.ok) {
        const data = await response.json();
        setUserData(data);
        console.log('User data:', data);
      } else {
        console.error('Failed to fetch user info');
      }
    } catch (error) {
      console.error('Error checking user info:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check group info
  const checkGroups = async () => {
    setLoading(true);
    try {
      const BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const response = await authFetch(`${BASE_URL}/api/auth/debug-groups/`);
      
      if (response.ok) {
        const data = await response.json();
        setGroupData(data);
        console.log('Group data:', data);
      } else {
        console.error('Failed to fetch group info');
      }
    } catch (error) {
      console.error('Error checking group info:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check CSRF token
  const checkCsrfToken = async () => {
    setCookies(document.cookie);
    
    try {
      const BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${BASE_URL}/api/auth/csrf/`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Received CSRF token:', data.csrfToken);
        setCsrfToken(data.csrfToken || 'No token received');
      } else {
        console.error('Failed to fetch CSRF token:', response.status);
        setCsrfToken('Failed to fetch token');
      }
    } catch (error) {
      console.error('Error fetching CSRF token:', error);
      setCsrfToken('Error fetching token');
    }
    
    // Check cookies again after fetch
    setTimeout(() => {
      setCookies(document.cookie);
    }, 500);
  };

  return (
    <div className="p-4 border border-gray-300 rounded mb-4 bg-blue-50">
      <h2 className="text-xl font-bold mb-4">Debugging Tools</h2>
      
      {/* User Debug Section */}
      <div className="mb-6">
        <h3 className="font-bold mb-2 text-lg border-b pb-1">User Information</h3>
        
        {userData ? (
          <div className="mb-2">
            <p><strong>Username:</strong> {userData.username}</p>
            <p><strong>ID:</strong> {userData.id}</p>
            <p><strong>Groups:</strong> {userData.groups.map(g => g.name).join(', ') || 'None'}</p>
            <p><strong>Is Staff:</strong> {userData.is_staff ? 'Yes' : 'No'}</p>
            <p><strong>Is Superuser:</strong> {userData.is_superuser ? 'Yes' : 'No'}</p>
          </div>
        ) : (
          <p className="mb-2">No user data loaded. Click the button below to check.</p>
        )}
        
        <button 
          onClick={checkUserInfo}
          className="bg-blue-500 text-white px-3 py-1 rounded mb-4"
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Check User Info'}
        </button>
      </div>
      
      {/* Group Debug Section */}
      <div className="mb-6">
        <h3 className="font-bold mb-2 text-lg border-b pb-1">Group Information</h3>
        
        {groupData ? (
          <div className="mb-2">
            <h4 className="font-semibold">All Groups in System:</h4>
            <ul className="list-disc list-inside mb-2 ml-4">
              {groupData.all_groups.map(g => (
                <li key={g.id}>{g.name} (ID: {g.id})</li>
              ))}
            </ul>
            
            <h4 className="font-semibold">Your Groups:</h4>
            {groupData.user_groups.length > 0 ? (
              <ul className="list-disc list-inside mb-2 ml-4">
                {groupData.user_groups.map(g => (
                  <li key={g.id}>{g.name} (ID: {g.id})</li>
                ))}
              </ul>
            ) : (
              <p className="mb-2 text-red-500">You are not in any groups!</p>
            )}
          </div>
        ) : (
          <p className="mb-2">No group data loaded. Click the button below to check.</p>
        )}
        
        <button 
          onClick={checkGroups}
          className="bg-blue-500 text-white px-3 py-1 rounded mb-4"
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Check Group Info'}
        </button>
      </div>
      
      {/* CSRF Debug Section */}
      <div className="mb-4">
        <h3 className="font-bold mb-2 text-lg border-b pb-1">CSRF Token Information</h3>
        
        <div className="mb-2">
          <p><strong>Current Cookies:</strong></p>
          <pre className="bg-gray-100 p-2 rounded overflow-x-auto max-h-24 text-xs">{cookies || 'None'}</pre>
        </div>
        
        <div className="mb-2">
          <p><strong>Current Token:</strong></p>
          <pre className="bg-gray-100 p-2 rounded overflow-x-auto max-h-24 text-xs">{csrfToken || 'None'}</pre>
        </div>
        
        <button 
          onClick={checkCsrfToken}
          className="bg-blue-500 text-white px-3 py-1 rounded"
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Check CSRF Token'}
        </button>
      </div>
    </div>
  );
}

export default Debug;
